import { shareAValue } from '../../common/components/utils/utils';

/**
 * Geolocation service that can get users location, resolve it or find distance to a point.
 *
 * TODO: Add the standard caching using local storage. Cache the location in some radius using Haversine formula.
 */
export class GeolocationService {
	constructor(success, error, initialize = true) {
		this.callbackFunction = success;
		this.erroCallback =
			error ||
			((err) => {
				console.error({ err });
			});

		this.bindEvents();
		this.setup();

		if (initialize) this.getGeolocation();
	}

	bindEvents() {
		['onError', 'onSuccess'].forEach((c) => {
			this[c] = this[c].bind(this);
		});
	}

	setup() {
		this.geocoderResponse = null;
		this.coords = null;
		this.geocoder = null;
	}

	/**
	 * Decode Google's address components.
	 * Address component is an array containing separate components for the given address.
	 *
	 * We loop through all different components and extract the connected types.
	 *
	 * @info Result type {@link https://developers.google.com/maps/documentation/geocoding/overview#results}
	 * @info Address components {@link https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types}
	 * @param addressComponents
	 */
	static parseAddress(addressComponents) {
		if (!addressComponents || !Array.isArray(addressComponents)) return {};

		const components = {
			home: ['street_number'],
			postal_code: ['postal_code'],
			street: ['street_address', 'route'],
			region: [
				'administrative_area_level_1',
				'administrative_area_level_2',
				'administrative_area_level_3',
				'administrative_area_level_4',
				'administrative_area_level_5',
			],
			city: [
				'locality',
				'sublocality',
				'postal_town',
				'sublocality_level_1',
				'sublocality_level_2',
				'sublocality_level_3',
				'sublocality_level_4',
			],
			country: ['country'],
		};

		// Properties to use short name with.
		const shorNameProperties = ['region'];

		const address = {
			home: '',
			postal_code: '',
			street: '',
			region: '',
			city: '',
			country: '',
		};

		addressComponents.forEach((component) => {
			for (const key in components) {
				if (shareAValue(components[key], component.types)) {
					if (shorNameProperties.includes(key)) {
						address[key] = component.short_name;
					} else {
						address[key] = component.long_name;
					}
				}
			}
		});

		return address;
	}

	/**
	 * Based on the coordinates decode and return the address.
	 * @param {GeolocationPosition.coords} coords
	 * @returns {Promise<*>}
	 */
	async decodeLocation(coords) {
		if (this.geocoder === null) this.geocoder = new google.maps.Geocoder();
		const latlng = {
			lat: parseFloat(coords.latitude),
			lng: parseFloat(coords.longitude),
		};
		if (this.coords === null) this.coords = latlng;
		if (this.geocoderResponse === null) {
			this.geocoderResponse = this.geocoder.geocode({ location: latlng });
			this.coords = latlng;
		}

		return this.geocoderResponse;
	}

	/**
	 * If there is an error with the geolocation.
	 * @param {GeolocationPositionError} e
	 */
	onError(e) {
		this.erroCallback(e);
	}

	/**
	 *
	 * @param {GeolocationPosition} e
	 */
	async onSuccess(e) {
		const reverse = await this.decodeLocation(e.coords);
		this.callbackFunction({
			// Return `this.coords` opposed to `e.cords` because it is in LatLng format.
			coords: this.coords,
			address: reverse.results?.[0]?.formatted_address || null,
		});
	}

	getGeolocation() {
		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(this.onSuccess, this.onError);
		}
	}

	/**
	 * Convert the response from matrix service to meaningful data.
	 *
	 * @example response
	 * {
	 *     "rows": [
	 *         {
	 *             "elements": [
	 *                 {
	 *                     "distance": {
	 *                         "text": "0.5 km",
	 *                         "value": 473
	 *                     },
	 *                     "duration": {
	 *                         "text": "2 mins",
	 *                         "value": 132
	 *                     },
	 *                     "status": "OK"
	 *                 }
	 *             ]
	 *         }
	 *     ],
	 *     "originAddresses": [
	 *         "Storgatan 4, 151 71 Södertälje, Sweden"
	 *     ],
	 *     "destinationAddresses": [
	 *         "Nygatan 30A, 151 72 Södertälje, Sweden"
	 *     ]
	 * }
	 *
	 * @param {object} response - {@link https://developers.google.com/maps/documentation/javascript/distancematrix#distance_matrix_responses}
	 * @param {string} status - {@link https://developers.google.com/maps/documentation/javascript/distancematrix#response-status-codes}
	 * @return {{}|{duration, mode, distance, from: *, to: *}}
	 */
	static parseDistanceMatrix(response, status, mode) {
		if (status !== 'OK') return {};

		const origins = response.originAddresses;
		const destinations = response.destinationAddresses;

		const element = response?.rows?.[0]?.elements?.[0];

		if (!element || !origins.length) return {};

		return {
			/** @see {@link https://developers.google.com/maps/documentation/javascript/distancematrix#distance_matrix_element_status_codes} */
			status: element.status,
			distance: element?.distance?.text,
			duration: element?.duration?.text,
			to: origins[0],
			from: destinations[0],
			mode,
		};
	}

	/**
	 * Calculate the distance and travel time from origin to destination based on the modes provided.
	 * @param {string|{lat:number,lng:number}} origin
	 * @param {string|{lat:number,lng:number}} destination
	 * @param {Function} callback
	 * @param {string[]} modes
	 * @return {Promise<void>}
	 */
	static async getDistanceToAddress(
		origin,
		destination,
		callback,
		modes = [
			google.maps.TravelMode.WALKING,
			google.maps.TravelMode.TRANSIT,
			google.maps.TravelMode.DRIVING,
		]
	) {
		const service = new google.maps.DistanceMatrixService();

		modes.forEach((mode) => {
			service.getDistanceMatrix(
				{
					origins: [origin],
					destinations: [destination],
					travelMode: mode,
				},
				(response, status) => {
					const data = GeolocationService.parseDistanceMatrix(response, status, mode);
					callback(data);
				}
			);
		});
	}
}
